import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Upload, CheckCircle, XCircle, Clock, FileCheck, Camera, FileText, Banknote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

const DOC_DEFS = [
  {
    key: 'id_doc',
    label: 'ID Document',
    description: 'Front photo of your South African ID, smart card, or valid passport.',
    icon: <FileText className="w-6 h-6" />,
    requiredFor: 'Tier 1',
  },
  {
    key: 'selfie',
    label: 'Selfie',
    description: 'A clear photo of your face. Used to match against your ID.',
    icon: <Camera className="w-6 h-6" />,
    requiredFor: 'Tier 1',
  },
  {
    key: 'poa',
    label: 'Proof of Address',
    description: 'Utility bill or bank statement (no older than 3 months).',
    icon: <FileCheck className="w-6 h-6" />,
    requiredFor: 'Tier 2',
  },
  {
    key: 'sof',
    label: 'Source of Funds',
    description: 'Payslip, employer letter, or bank statement showing income.',
    icon: <Banknote className="w-6 h-6" />,
    requiredFor: 'Tier 3',
  },
];

const ACCEPT = 'image/jpeg,image/png,application/pdf';

const statusPill = (status) => {
  const cls = {
    approved: 'bg-emerald-100 text-emerald-700',
    pending:  'bg-amber-100 text-amber-700',
    rejected: 'bg-rose-100 text-rose-700',
  }[status] || 'bg-slate-100 text-slate-600';
  const Icon = {
    approved: CheckCircle,
    pending:  Clock,
    rejected: XCircle,
  }[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${cls}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const KycUploadPage = () => {
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);  // doc_type currently being uploaded
  const fileRefs = useRef({});

  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');
  const isLoggedIn = !!user?.id;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [tierR, docsR] = await Promise.all([
        axios.get(`${API}/users/me/kyc-tier`),
        axios.get(`${API}/kyc/documents`),
      ]);
      setTier(tierR.data);
      setDocs(docsR.data || []);
    } catch (e) {
      toast.error('Failed to load KYC status');
    }
  };

  const docByType = (t) => {
    const matching = (docs || []).filter((d) => d.doc_type === t);
    if (matching.length === 0) return null;
    // newest first
    return matching.sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''))[0];
  };

  const upload = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10 MB cap');
      return;
    }
    setUploading(docType);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(
        `${API}/kyc/documents?doc_type=${encodeURIComponent(docType)}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`${docType} uploaded — awaiting review`);
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0FF] to-white p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/customer/dashboard')}
          className="mb-4"
          data-testid="kyc-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>

        <Card className="mb-6 bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white">
          <CardHeader>
            <CardTitle className="text-2xl">KYC Verification</CardTitle>
            <CardDescription className="text-white/70">
              Higher tiers unlock larger transaction limits. Documents are reviewed by our compliance team.
            </CardDescription>
          </CardHeader>
          {tier && !tier.exempt && (
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-white/60 text-xs">Current tier</p>
                  <p className="text-lg font-bold" data-testid="kyc-current-tier">{tier.tier}</p>
                </div>
                <div>
                  <p className="text-white/60 text-xs">Daily limit</p>
                  <p className="text-lg font-bold">R{(tier.daily_limit_zar || 0).toLocaleString('en-ZA')}</p>
                </div>
                <div>
                  <p className="text-white/60 text-xs">Per transaction</p>
                  <p className="text-lg font-bold">R{(tier.per_txn_limit_zar || 0).toLocaleString('en-ZA')}</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        <div className="space-y-3">
          {DOC_DEFS.map((def) => {
            const existing = docByType(def.key);
            const inputId = `kyc-file-${def.key}`;
            return (
              <Card key={def.key} data-testid={`kyc-row-${def.key}`}>
                <CardContent className="pt-4 flex items-center gap-4">
                  <div className="text-[#1B0C34]">{def.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{def.label}</p>
                      <span className="text-xs text-slate-500">required for {def.requiredFor}</span>
                      {existing && statusPill(existing.status)}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{def.description}</p>
                    {existing?.review_reason && (
                      <p className="text-xs text-rose-600 mt-1">
                        Reviewer note: {existing.review_reason}
                      </p>
                    )}
                  </div>
                  <input
                    id={inputId}
                    ref={(el) => { fileRefs.current[def.key] = el; }}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => upload(def.key, e.target.files?.[0])}
                    data-testid={`kyc-file-input-${def.key}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileRefs.current[def.key]?.click()}
                    disabled={uploading === def.key}
                    data-testid={`kyc-upload-btn-${def.key}`}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploading === def.key ? 'Uploading…' : (existing ? 'Replace' : 'Upload')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 mt-6 text-center">
          Documents are kept securely and used only for verification. Reviews typically take 1–2 business days.
        </p>
      </div>
    </div>
  );
};

export default KycUploadPage;
